'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

import LoadingAnimation from '@/components/loading-animation';
import { completeFirebaseSocialLogin } from '@/lib/auth/complete-firebase-social-login';
import {
    clearLoginPending,
    readLoginPending,
    readOAuthLoginTrackingContext,
    readReturnToForOAuth,
    sanitizeReturnToForReplace,
} from '@/lib/auth/firebase-oauth-session';
import { getFirebaseRedirectResult, resetFirebaseRedirectResultCache } from '@/lib/firebase/auth';
import { useToast } from '@/providers/root/toast';
import { readArmsFailDetail } from '@/utils/arms/common';
import { reportLoginFail } from '@/utils/arms/login-events';
import { trackCustom, ETrackerEvents } from '@/utils/tracker';

const DEFAULT_AFTER_LOGIN = '/profile';

let hasProcessedRedirectResult = false;

/**
 * 全站只挂一处：Google OAuth redirect 回站后，统一 getRedirectResult + 业务登录 + 按 returnTo 导航。
 * Facebook 已改为 JS SDK + signInWithCredential，不再走 redirect。
 */
export default function FirebaseLoginRedirectHandler() {
    const router = useRouter();
    const { addToast } = useToast();
    const tLogin = useTranslations('ProfileLogin');
    const [isLoading, setIsLoading] = useState(readLoginPending);

    useEffect(() => {
        if (hasProcessedRedirectResult) {
            return;
        }
        hasProcessedRedirectResult = true;

        (async () => {
            try {
                const credential = await getFirebaseRedirectResult();
                if (!credential) {
                    return;
                }
                const tracking = readOAuthLoginTrackingContext();
                setIsLoading(true);
                const idToken = await credential.user.getIdToken();
                const stored = readReturnToForOAuth();
                const result = await completeFirebaseSocialLogin({
                    idToken,
                    tracking,
                    onErrorToast: addToast,
                });
                if (!result.success) {
                    return;
                }
                const to = sanitizeReturnToForReplace(stored, DEFAULT_AFTER_LOGIN);
                router.replace(to);
            } catch (err) {
                console.error('firebase redirect result error', err);
                const t = readOAuthLoginTrackingContext();
                trackCustom(ETrackerEvents.LOGIN_RESULT, {
                    trigger_scene: t.trigger_scene,
                    login_method: t.login_method,
                    result_status: 'fail',
                });
                if (t.login_method) {
                    reportLoginFail({
                        login_method: t.login_method,
                        trigger_scene: t.trigger_scene,
                        fail_reason: 'google_redirect_result_failed',
                        fail_detail: readArmsFailDetail(err),
                    });
                }
                addToast(tLogin('profile_login_failed'));
            } finally {
                // 根布局不随路由切换卸载，成功 replace 后也必须关 loading
                setIsLoading(false);
                clearLoginPending();
                resetFirebaseRedirectResultCache();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅处理一次 OAuth 回跳
    }, []);

    return <LoadingAnimation visible={isLoading} />;
}
